import { useState, useEffect, useMemo } from 'react'
import type { Order } from '../../types'
import { config } from '../../config'
import { adminApi, adminOrderToOrder } from '../../lib/api'
import { layout, cx, text, ui } from '../../lib/ui-primitives'
import { Button } from '../primitives'
import { XIcon } from '../icons'
import {
  DashboardHeader,
  StatsGrid,
  FilterBar,
  OrderList,
  OrderDetailPanel,
  type FilterType,
  type DashboardStats,
} from './components'

// ============================================
// ADMIN DASHBOARD
// Order management dashboard
// ============================================

interface AdminDashboardProps {
  onExit: () => void
}

/**
 * AdminDashboard - Order management for admin users
 *
 * Refactored to use extracted components:
 * - DashboardHeader (navigation)
 * - StatsGrid (statistics)
 * - FilterBar (status filtering)
 * - OrderList (order cards)
 * - OrderDetailPanel (order actions)
 *
 * LOC reduced from 437 to ~150 (66% reduction)
 */
export function AdminDashboard({ onExit }: AdminDashboardProps) {
  // State
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [filter, setFilter] = useState<FilterType>('all')
  const [txHash, setTxHash] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [apiError, setApiError] = useState('')

  // Load orders
  const loadOrders = async () => {
    setLoading(true)
    setApiError('')
    const result = await adminApi.getOrders({ limit: 100 })
    if (result.success && result.data) {
      setOrders(result.data.orders.map(adminOrderToOrder))
    } else {
      setApiError(result.error || 'Failed to load orders')
      setOrders([])
    }
    setLoading(false)
  }

  useEffect(() => {
    loadOrders()
  }, [])

  // Computed values
  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders
    return orders.filter((o) => o.status === filter)
  }, [orders, filter])

  const stats: DashboardStats = useMemo(() => {
    const today = new Date().setHours(0, 0, 0, 0)
    const todayOrders = orders.filter((o) => o.createdAt >= today)
    return {
      total: orders.length,
      pending: orders.filter((o) => o.status === 'pending').length,
      approved: orders.filter((o) => o.status === 'approved' || o.status === 'completed').length,
      rejected: orders.filter((o) => o.status === 'rejected').length,
      todayCount: todayOrders.length,
      todayVolume: todayOrders.reduce((sum, o) => sum + o.amountMYR, 0),
    }
  }, [orders])

  // Action handlers
  const handleApprove = async (order: Order) => {
    setActionLoading(true)
    setApiError('')
    const result = await adminApi.approveOrder(order.id)
    if (result.success) {
      await loadOrders()
      setSelectedOrder({ ...order, status: 'approved' })
    } else {
      setApiError(result.error || 'Failed to approve order')
    }
    setActionLoading(false)
  }

  const handleReject = async (order: Order) => {
    setActionLoading(true)
    setApiError('')
    const result = await adminApi.rejectOrder(order.id)
    if (result.success) {
      await loadOrders()
      setSelectedOrder({ ...order, status: 'rejected' })
    } else {
      setApiError(result.error || 'Failed to reject order')
    }
    setActionLoading(false)
  }

  const handleComplete = async (order: Order, hash: string) => {
    if (!hash.trim()) return
    setActionLoading(true)
    setApiError('')
    const result = await adminApi.completeOrder(order.id, hash.trim())
    if (result.success) {
      await loadOrders()
      setSelectedOrder({ ...order, status: 'completed', txHash: hash.trim() })
      setTxHash('')
    } else {
      setApiError(result.error || 'Failed to complete order')
    }
    setActionLoading(false)
  }

  return (
    <div className={cx(layout.page, 'min-h-screen')}>
      {/* Header */}
      <DashboardHeader
        title={`${config.businessName} Admin`}
        subtitle="Order Management Dashboard"
        onRefresh={loadOrders}
        onExit={onExit}
      />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <StatsGrid stats={stats} />

        {/* API Error Alert */}
        {apiError && (
          <ErrorAlert message={apiError} onDismiss={() => setApiError('')} />
        )}

        {/* Filters */}
        <FilterBar
          filter={filter}
          onFilterChange={setFilter}
          pendingCount={stats.pending}
        />

        {/* Orders Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Order List */}
          <div>
            <h3 className={cx(text.h4, 'mb-3')}>
              Orders ({filteredOrders.length})
            </h3>
            <OrderList
              orders={filteredOrders}
              selectedOrderId={selectedOrder?.id}
              onSelectOrder={setSelectedOrder}
              loading={loading}
            />
          </div>

          {/* Order Detail */}
          <div>
            <OrderDetailPanel
              order={selectedOrder}
              onClose={() => setSelectedOrder(null)}
              onApprove={handleApprove}
              onReject={handleReject}
              onComplete={handleComplete}
              txHash={txHash}
              onTxHashChange={setTxHash}
              actionLoading={actionLoading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// ERROR ALERT
// ============================================

interface ErrorAlertProps {
  message: string
  onDismiss: () => void
}

function ErrorAlert({ message, onDismiss }: ErrorAlertProps) {
  return (
    <div
      className={cx(
        ui.card,
        'bg-red-500/10 border-red-500/30 p-4 mb-4'
      )}
      role="alert"
    >
      <div className="flex items-center justify-between">
        <span className="text-red-400">{message}</span>
        <Button
          variant="quiet"
          size="sm"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          <XIcon />
        </Button>
      </div>
    </div>
  )
}
